import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Sparkles, ChevronRight, ArrowUpRight, Zap, Clock, Bot, Mail } from "lucide-react";
import { toast } from "sonner";
import { relativeTime, fmtSignal } from "@/lib/utils";

const PRIO_COLOR = { high: "text-destructive border-destructive/40 bg-destructive/10", medium: "text-amber-accent border-amber-accent/40 bg-amber-accent/10", low: "text-muted-foreground border-border bg-muted/30" };

export default function AutopilotPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadHistory = () => api.get("/autopilot/runs").then((r) => setHistory(r.data.runs || []));
  const loadCfg = () => api.get("/autopilot/settings").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); loadCfg(); }, []);

  const saveCfg = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.post("/autopilot/settings", patch);
      setCfg((c) => ({ ...c, ...data }));
      toast.success(patch.active === undefined ? "Autopilot settings saved" : (data.auto_approve_active ? "Autopilot ON — verified high-fit prospects auto-enroll" : "Autopilot paused"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const sendDigest = async () => {
    try { await api.post("/autopilot/send-digest-now"); toast.success("Daily digest sent to your inbox"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send digest"); }
  };

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/autopilot/scan?limit=6");
      setSnapshot(data);
      toast.success(`Autopilot recommended ${data.recommendations.length} actions`);
      loadHistory();
    } catch { toast.error("Autopilot scan failed"); }
    setBusy(false);
  };

  const enqueue = async (rec) => {
    try {
      await api.post("/linkedin/queue/enqueue", {
        action_type: rec.recommendation.action === "send_followup" ? "message" : "message",
        payload: { body: rec.recommendation.draft_message || "" },
        prospect_id: rec.prospect_id,
        priority: rec.recommendation.priority || "normal",
      });
      toast.success(`Queued ${rec.recommendation.action} for ${rec.prospect_name}`);
    } catch { toast.error("Could not enqueue"); }
  };

  const items = snapshot?.recommendations || (history[0]?.recommendations) || [];

  return (
    <DashboardLayout
      eyebrow="AI Decision Autonomy"
      title="Autopilot"
      actions={
        <Button onClick={scan} disabled={busy} data-testid="autopilot-scan-btn" className="rounded-sm">
          <Sparkles className="w-4 h-4 mr-1.5" />{busy ? "Scanning..." : "Run scan"}
        </Button>
      }
    >
      <div className="data-card mb-6 bg-card/40" data-testid="auto-approve-card">
        <div className="flex items-start gap-3">
          <Bot className="w-5 h-5 text-signal-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="font-display font-semibold text-base">Hands-free auto-approval</div>
              <Switch
                data-testid="auto-approve-toggle"
                checked={!!cfg?.auto_approve_active}
                disabled={saving}
                onCheckedChange={(v) => saveCfg({ active: v })}
              />
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              When ON, prospects scoring at/above the bar <strong>that have a verified email or resolved LinkedIn</strong> are auto-approved into outreach (up to the daily cap) — no manual clicking. Everything else stays in your review queue.
            </div>
            <div className="flex flex-wrap items-end gap-4 mt-4">
              <label className="text-xs text-muted-foreground">
                Min score
                <Input type="number" min="0" max="100" defaultValue={cfg?.min_score ?? 85}
                  data-testid="auto-approve-minscore"
                  onBlur={(e) => saveCfg({ min_score: parseInt(e.target.value, 10) })}
                  className="h-8 w-24 mt-1" />
              </label>
              <label className="text-xs text-muted-foreground">
                Daily cap
                <Input type="number" min="1" max="500" defaultValue={cfg?.daily_cap ?? 25}
                  data-testid="auto-approve-cap"
                  onBlur={(e) => saveCfg({ daily_cap: parseInt(e.target.value, 10) })}
                  className="h-8 w-24 mt-1" />
              </label>
              <div className="text-xs text-muted-foreground">
                <div>Approved today: <span className="font-mono font-semibold text-foreground">{cfg?.approved_today ?? 0}</span></div>
                <div>In review queue: <span className="font-mono font-semibold text-foreground">{cfg?.pending_approval ?? 0}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={sendDigest} data-testid="send-digest-btn" className="ml-auto">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Email me a digest now
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="data-card mb-6 flex items-start gap-3 bg-card/40">
        <Zap className="w-5 h-5 text-signal-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-semibold text-base">Next-best-action engine</div>
          <div className="text-sm text-muted-foreground mt-1">
            Scans your highest-scoring prospects, factors in recency &amp; status &amp; logistics signals, and invokes the Engagement Agent in parallel to recommend the next move. One-click queue the action to your LinkedIn worker.
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <div className="data-card text-center py-12 text-sm text-muted-foreground">
          Click <span className="font-mono">Run scan</span> to generate AI recommendations across your top prospects.
        </div>
      )}

      <div className="space-y-3">
        {items.map((r, i) => {
          const p = r.recommendation || {};
          const prio = p.priority || "low";
          return (
            <div key={r.prospect_id} className="data-card" data-testid={`autopilot-rec-${i}`}>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-12 md:col-span-3">
                  <Link to={`/app/prospects/${r.prospect_id}`} className="font-display font-semibold tracking-tight hover:underline inline-flex items-center gap-1">
                    {r.prospect_name} <ArrowUpRight className="w-3 h-3" />
                  </Link>
                  <div className="text-xs text-muted-foreground">{r.title}</div>
                  <div className="text-xs text-muted-foreground">{r.company}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(r.signals || []).slice(0, 2).map((s) => <span key={s} className="signal-pill">{fmtSignal(s)}</span>)}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className={`px-2 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border ${PRIO_COLOR[prio]}`}>{prio}</span>
                    <span className="signal-pill">{p.action?.replace(/_/g, " ")}</span>
                    <span className="signal-pill">{p.channel}</span>
                    <span className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> last touch: {r.hours_since_touch > 168 ? "never" : `${r.hours_since_touch}h ago`}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-2 italic">"{p.rationale}"</div>
                  {p.draft_message && (
                    <pre className="text-xs whitespace-pre-wrap font-sans bg-muted/30 border border-border rounded-sm p-3 max-h-48 overflow-y-auto" data-testid={`autopilot-draft-${i}`}>{p.draft_message}</pre>
                  )}
                </div>
                <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 md:items-end">
                  <div className="md:text-right">
                    <div className="font-mono text-2xl font-semibold text-signal-500">{r.score}</div>
                    <div className="text-[0.6rem] tracking-widest uppercase text-muted-foreground">score</div>
                  </div>
                  <Button size="sm" onClick={() => enqueue(r)} data-testid={`autopilot-enqueue-${i}`} className="w-full md:w-auto">
                    Queue action <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {snapshot && <div className="mt-6 text-xs text-muted-foreground text-right">Scan completed {relativeTime(snapshot.ran_at)}</div>}
    </DashboardLayout>
  );
}
